import { Flex, Select, Skeleton, Table, type TableProps } from "antd";
import type { AnyObject } from "antd/es/_util/type";
import type { ReactNode } from "react";
import type { ColumnsType } from "../types/global.types";
import { cn } from "../utils/global.utils";
import CustomPagination from "./CustomPagination";

interface CustomTableProps<T extends AnyObject> extends TableProps<T> {
  /** Current page number */
  tableTitle?: string;
  description?: string;
  customTitle?: ReactNode;
  icon?: ReactNode;
  /** Current page number */
  page?: number;
  /** Number of items per page */
  limit?: number;
  /** Total number of records */
  total?: number;
  /** Columns configuration */
  columns: ColumnsType<T>;
  /** Optional filter or control section (top-right area) */
  filterSection?: ReactNode;
  /** Hide row selector and total rows section */
  hideRowSelect?: boolean;
  /** Hide pagination footer */
  hidePagination?: boolean;
  /** Disable alternating row colors */
  hideDifferentRowColor?: boolean;
  /** Outer container class */
  rootClassName?: string;
  /** Custom top section class */
  topSectionClassName?: string;
  /** Table root class */
  tableRootClassName?: string;
  /** Pagination handler */
  onPaginate?: (page: number, limit: number) => void;
  /** Limit change handler */
  onLimitChange?: (val: number) => void;
}

const CustomTable = <T extends AnyObject>({
  loading,
  page,
  limit,
  total,
  customTitle,
  dataSource,
  columns,
  filterSection,
  hideRowSelect = false,
  hidePagination = false,
  hideDifferentRowColor = true,
  className,
  rootClassName,
  topSectionClassName,
  tableRootClassName,
  onLimitChange,
  onPaginate,
  ...rest
}: CustomTableProps<T>) => {
  const hasData = Boolean(dataSource?.length);
  const showTopSection = (!hideRowSelect && total) || filterSection;
  const showPagination = !hidePagination && hasData && total && limit;

  // Generate skeleton data based on column length
  const getTableData = () => {
    if (loading) {
      return Array.from({ length: limit || 10 }, (_, index) => ({
        key: `skeleton-${index}`,
        _id: `skeleton-${index}`,
      })) as unknown as T[];
    }
    return dataSource;
  };

  const tableData = getTableData();

  // Create skeleton columns
  const tableColumns = loading
    ? columns.map((col) => ({
        ...col,
        render: () => <Skeleton.Input active size="small" block />,
      }))
    : columns;

  return (
    <div
      className={cn(
        "p-6 bg-white rounded-xl border border-theme-gray-90 dark:bg-theme-slate-800 dark:border-theme-slate-800",
        rootClassName
      )}
    >
      {showTopSection && (
        <Flex
          align="end"
          justify="space-between"
          className={cn("p-4 w-full", topSectionClassName)}
        >
          <div>
            {!hideRowSelect && (
              <div className="flex items-center gap-2 text-theme-gray-40 font-medium">
                <span className="text-nowrap">Total Rows</span>
                <Select
                  size="small"
                  style={{ minWidth: 75, textAlign: "center" }}
                  defaultValue={limit}
                  onChange={onLimitChange}
                  options={[10, 20, 50, 100, 1000].map((value) => ({
                    label: value.toString(),
                    value,
                  }))}
                />
                <span className="text-nowrap">of {total || 0}</span>
              </div>
            )}
          </div>

          {filterSection && (
            <div className="flex items-center gap-2">{filterSection}</div>
          )}
        </Flex>
      )}
      {!!customTitle && (
        <h4 className="text-white text-3xl font-bold pb-4">{customTitle}</h4>
      )}
      {/* Table Section */}
      <div
        className={cn(
          "border border-gray-700 rounded-md px-4 custom-scroll overflow-x-auto",
          tableRootClassName
        )}
      >
        <Table
          loading={false}
          columns={tableColumns}
          dataSource={tableData}
          pagination={false}
          rowKey={(record) => record._id || record.id || record.sl}
          className={cn("custom-table ", className, {
            "different-row-color": !hideDifferentRowColor && hasData,
            "table-rounded-top": !showTopSection,
            "table-hide-border-bottom": !showPagination,
          })}
          {...rest}
        />

        {showPagination && (
          <CustomPagination
            total={total}
            limit={limit}
            page={page}
            hidePagination={hidePagination}
            handlePagination={onPaginate}
          />
        )}
      </div>
    </div>
  );
};

export default CustomTable;
